/* global axios */

const knowledgeProxy = (accountId, path) =>
  `/api/v1/accounts/${accountId}/proxy/knowledge${path}`;

export default {
  namespaced: true,

  state: {
    list: [],
    loading: false,
    error: null,

    pagination: {
      page: 1,
      pageSize: 50,
      total: 0,
    },

    filters: {
      keyword: '',
      branchIds: [],
      staffId: null,
      from: null,
      to: null,
    },
  },

  getters: {
    all: state => state.list,
    isLoading: state => state.loading,
    error: state => state.error,
    pagination: state => state.pagination,

    filtered: state => {
      let result = state.list;

      if (state.filters.keyword) {
        const q = state.filters.keyword.toLowerCase();
        result = result.filter(a =>
          String(a.id).toLowerCase().includes(q) ||
          String(a.staffName).toLowerCase().includes(q) ||
          String(a.branchName).toLowerCase().includes(q)
        );
      }

      if (state.filters.branchIds.length > 0) {
        result = result.filter(a => state.filters.branchIds.includes(a.branchId));
      }

      if (state.filters.staffId) {
        result = result.filter(a => a.staffId === state.filters.staffId);
      }

      if (state.filters.from) {
        result = result.filter(a => a.startDate >= state.filters.from);
      }
      if (state.filters.to) {
        result = result.filter(a => !a.endDate || a.endDate <= state.filters.to);
      }

      return result;
    },

    getById: state => id => state.list.find(a => a.id === id),
    byBranch: state => branchId => state.list.filter(a => a.branchId === branchId),
    byStaff: state => staffId => state.list.filter(a => a.staffId === staffId),
  },

  mutations: {
    SET_LOADING(state, value) { state.loading = value; },
    SET_ERROR(state, error) { state.error = error; },
    SET_ASSIGNMENTS(state, { data, pagination }) {
      state.list = data;
      if (pagination) state.pagination = pagination;
    },
    ADD_ASSIGNMENT(state, assignment) { state.list.unshift(assignment); },
    ADD_ASSIGNMENTS(state, assignments) { state.list.unshift(...assignments); },
    UPDATE_ASSIGNMENT(state, assignment) {
      const index = state.list.findIndex(a => a.id === assignment.id);
      if (index !== -1) {
        state.list.splice(index, 1, assignment);
      }
    },
    REMOVE_ASSIGNMENT(state, id) { state.list = state.list.filter(a => a.id !== id); },
    REMOVE_ASSIGNMENTS(state, ids) { state.list = state.list.filter(a => !ids.includes(a.id)); },
    SET_FILTERS(state, filters) { state.filters = { ...state.filters, ...filters }; },
    CLEAR_FILTERS(state) {
      state.filters = { keyword: '', branchIds: [], staffId: null, from: null, to: null };
    },
    SET_PAGE(state, page) { state.pagination.page = page; },
    SET_PAGE_SIZE(state, pageSize) { state.pagination.pageSize = pageSize; },
  },

  actions: {
    async fetchAll({ commit, state, rootGetters }) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);

      const accountId = rootGetters.getCurrentAccountId;

      try {
        const params = {
          q: state.filters.keyword || null,
          branchId: state.filters.branchIds[0] || null,
          staffId: state.filters.staffId || null,
          from: state.filters.from || null,
          to: state.filters.to || null,
          page: state.pagination.page,
          pageSize: state.pagination.pageSize,
        };

        const response = await axios.get(
          knowledgeProxy(accountId, `/v2/accounts/${accountId}/assignments`),
          { params }
        );

        const rawData = response.data.results || [];
        const data = rawData.map(item => ({
          id: item.assignment_id,
          accountId: item.account_id,
          branchId: item.branch_id,
          staffId: item.staff_id,
          startDate: item.start_date,
          endDate: item.end_date,
          branchName: item.branch_name,
          staffName: item.staff_name,
          createdAt: item.created_at,
          updatedAt: item.updated_at,
          createdBy: item.created_by,
          meta: item.meta,
          timeSlots: (item.time_slots || []).map(slot => ({
            id: slot.id,
            startTime: slot.start_time,
            endTime: slot.end_time,
            createdAt: slot.created_at,
          })),
        }));

        const pagination = {
          page: response.data.page || params.page,
          pageSize: params.pageSize,
          total: response.data.total || data.length,
          next: response.data.next || -1,
        };

        commit('SET_ASSIGNMENTS', { data, pagination });
        return data;
      } catch (err) {
        commit('SET_ERROR', err.response?.data || err.message);
        throw err;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async create({ commit, rootGetters }, payload) {
      const accountId = rootGetters.getCurrentAccountId;
      try {
        const response = await axios.post(
          knowledgeProxy(accountId, `/v2/accounts/${accountId}/assignments`),
          payload
        );
        commit('ADD_ASSIGNMENT', response.data);
        return response.data;
      } catch (err) {
        if (err.response?.status === 409) {
          throw new Error('ASSIGNMENT_CONFLICT');
        }
        throw err;
      }
    },

    async bulkCreate({ commit, rootGetters }, assignments) {
      const accountId = rootGetters.getCurrentAccountId;
      try {
        const response = await axios.post(
          knowledgeProxy(accountId, `/v2/accounts/${accountId}/assignments/bulk`),
          { assignments }
        );
        commit('ADD_ASSIGNMENTS', response.data);
        return response.data;
      } catch (err) {
        throw err;
      }
    },

    async update({ commit, rootGetters }, { id, payload }) {
      const accountId = rootGetters.getCurrentAccountId;
      try {
        const response = await axios.put(
          knowledgeProxy(accountId, `/v2/accounts/${accountId}/assignments/${id}`),
          payload
        );
        commit('UPDATE_ASSIGNMENT', response.data);
        return response.data;
      } catch (err) {
        if (err.response?.status === 409) {
          throw new Error('ASSIGNMENT_CONFLICT');
        }
        throw err;
      }
    },

    async delete({ commit, rootGetters }, id) {
      const accountId = rootGetters.getCurrentAccountId;
      try {
        await axios.delete(
          knowledgeProxy(accountId, `/v2/accounts/${accountId}/assignments/${id}`)
        );
        commit('REMOVE_ASSIGNMENT', id);
      } catch (err) {
        throw err;
      }
    },

    async bulkDelete({ commit, dispatch, rootGetters }, payload) {
      const accountId = rootGetters.getCurrentAccountId;
      try {
        await axios.delete(
          knowledgeProxy(accountId, `/v2/accounts/${accountId}/assignments/bulk`),
          { data: payload }
        );
        if (payload.ids?.length) {
          commit('REMOVE_ASSIGNMENTS', payload.ids);
        } else {
          await dispatch('fetchAll');
        }
      } catch (err) {
        throw err;
      }
    },

    async split({ commit, rootGetters }, { assignmentId, payload }) {
      const accountId = rootGetters.getCurrentAccountId;
      try {
        const response = await axios.post(
          knowledgeProxy(accountId, `/v2/accounts/${accountId}/assignments/${assignmentId}/split`),
          payload
        );
        commit('REMOVE_ASSIGNMENT', assignmentId);
        if (response.data.resulting_assignments?.length) {
          commit('ADD_ASSIGNMENTS', response.data.resulting_assignments);
        }
        return response.data;
      } catch (err) {
        throw err;
      }
    },

    setFilters({ commit }, filters) { commit('SET_FILTERS', filters); },
    clearFilters({ commit }) { commit('CLEAR_FILTERS'); },
    setPage({ commit }, page) { commit('SET_PAGE', page); },
    setPageSize({ commit }, size) { commit('SET_PAGE_SIZE', size); },
  },
};
